import React from 'react'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import * as yup from 'yup'
import produce from 'immer'
import _ from 'lodash'

import { COUNTRY } from '@qflow/constants'

import { FormInput, FormItem } from 'portal/components/RecordEditing'
import { ClientKnownAddressInput } from './ClientKnownAddressInput'

import {
  CheckAddress,
  CheckCarrierLicence,
  CheckCarrierName,
  CheckConsignmentOrDeliveryNumber,
  CheckDate1isBeforeDate2,
  CheckNotes,
  CheckReceiverOrSupplierOrSenderOrCarrierReference,
  CheckSubContractor,
  CheckVehicleReg,
  CheckWasteFacilityOperator,
  CheckWastePermit,
  CheckWasteSICCode
} from 'portal/lib/functions/SimpleDataValidation'
import {
  SubSection,
  SubSectionHeading
} from 'portal/components/RecordsModules/common/StyledComponents'

import { useCountryDrivenValues } from 'portal/lib/hooks'

import { Input as PrimitiveInput } from 'portal/lib/primitives'
import { dateValidator } from 'portal/lib/primitives/forms/Validators'

import { EditWasteCarrierSelector } from './EditWasteCarrierSelector'
import { EditWasteFacilitySelector } from './EditWasteFacilitySelector'

export function Waste() {
  const form = useFormContext()
  const { text, wasteFields } = useCountryDrivenValues()

  const dataSourceTracking = useSelector(
    state => state.records.details.item.dataSourceTracking
  )
  const projectCountry = useSelector(
    state => state.projectDetails.modules?.country
  )
  const projectTimeZone = useSelector(
    state => state.projectDetails.modules.timeZone
  )

  return (
    <>
      <SubSection style={{ marginTop: '1.25rem' }}>
        <SubSectionHeading>General</SubSectionHeading>
        <FormItem
          label="Arrival date and time"
          valueAdd={{
            fieldName: 'removalDate',
            ...dataSourceTracking?.removalDate
          }}
        >
          <FormInput.DateTimeCells
            name="data.removalDate"
            rules={dateValidator(
              form.getValues,
              'data.removalDate',
              true,
              'Collection Arrival'
            )}
          />
        </FormItem>

        <FormItem
          label="Exit date and time"
          valueAdd={{
            fieldName: 'removalExitDate',
            ...dataSourceTracking?.removalExitDate
          }}
        >
          <FormInput.DateTimeCells
            name="data.removalExitDate"
            rules={dateValidator(form.getValues, 'data.removalExitDate')}
            simpleDataValidationRules={v =>
              CheckDate1isBeforeDate2(
                'Collection Arrival',
                form.getValues('data.removalDate'),
                'Collection Exit',
                form.getValues('data.removalExitDate')
              )
            }
          />
        </FormItem>

        {wasteFields && wasteFields.consignmentId && (
          <FormItem
            label={text.consignmentNumber}
            valueAdd={{
              fieldName: 'consignmentId',
              ...dataSourceTracking?.consignmentId
            }}
          >
            <FormInput.Text
              label="Consignment number"
              name="data.consignmentId"
              rules={{
                required: 'Consignment Number is mandatory'
              }}
              simpleDataValidationRules={CheckConsignmentOrDeliveryNumber}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.receiverReferenceNumber && (
          <FormItem
            label="Sender reference"
            valueAdd={{
              fieldName: 'receiverReferenceNumber',
              ...dataSourceTracking?.receiverReferenceNumber
            }}
          >
            <FormInput.Text
              label="Sender reference"
              name="data.receiverReferenceNumber"
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.subcontractorName && (
          <FormItem
            label="Subcontractor"
            valueAdd={{
              fieldName: 'subcontractorName',
              ...dataSourceTracking?.subcontractorName
            }}
          >
            <FormInput.Text
              label="Subcontractor"
              name="data.subcontractorName"
              simpleDataValidationRules={CheckSubContractor}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.originSiteAddressKnown && (
          <ClientKnownAddressInput
            addressName="Origin Site"
            otherAddressName="Facility Site"
            form={form}
            knownlabel={'Origin site address'}
            knownPropertyPath={'data.originSiteAddressKnown'}
            enteredPropertyPath={'data.originSiteAddressEntered'}
            coordinatesPropertyPath={'data.originSiteAddressCoordinates'}
            valueAdd={{
              fieldName: 'originSiteAddressKnown',
              ...dataSourceTracking?.originSiteAddressKnown
            }}
          />
        )}

        {wasteFields && wasteFields.classificationCode && (
          <FormItem
            label="SIC code"
            valueAdd={{
              fieldName: 'classificationCode',
              ...dataSourceTracking?.classificationCode
            }}
          >
            <FormInput.Text
              label="SIC code"
              name="data.classificationCode"
              simpleDataValidationRules={CheckWasteSICCode}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.vehicleRegistration && (
          <FormItem
            label="Vehicle reg"
            valueAdd={{
              fieldName: 'vehicleRegistration',
              ...dataSourceTracking?.vehicleRegistration
            }}
          >
            <FormInput.Text
              label="Vehicle reg"
              name="data.vehicleRegistration"
              simpleDataValidationRules={CheckVehicleReg}
            />
          </FormItem>
        )}
      </SubSection>
      <SubSection style={{ marginTop: '1.25rem' }}>
        <SubSectionHeading>Carrier</SubSectionHeading>

        {projectCountry === COUNTRY.UK && (
          <FormItem label={`${text.carrier} lookup`}>
            <EditWasteCarrierSelector />
          </FormItem>
        )}

        {wasteFields && wasteFields.carrierName && (
          <FormItem
            label={text.carrierName}
            valueAdd={{
              fieldName: 'carrierName',
              ...dataSourceTracking?.carrierName
            }}
          >
            <FormInput.Text
              label={text.carrierName}
              name="data.carrierName"
              simpleDataValidationRules={CheckCarrierName}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.carrierLicenceId && (
          <FormItem
            label="Licence"
            valueAdd={{
              fieldName: 'carrierLicenceId',
              ...dataSourceTracking?.carrierLicenceId
            }}
          >
            <FormInput.Text
              label="Licence"
              name="data.carrierLicenceId"
              simpleDataValidationRules={CheckCarrierLicence}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.supplierReferenceNumber && (
          <FormItem
            label="Reference"
            valueAdd={{
              fieldName: 'supplierReferenceNumber',
              ...dataSourceTracking?.supplierReferenceNumber
            }}
          >
            <FormInput.Text
              label="Reference"
              name="data.supplierReferenceNumber"
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
            />
          </FormItem>
        )}
      </SubSection>
      <SubSection style={{ marginTop: '1.25rem' }}>
        <SubSectionHeading>Facility</SubSectionHeading>

        {projectCountry === COUNTRY.UK && (
          <FormItem label="Facility lookup">
            <EditWasteFacilitySelector
              removalDate={PrimitiveInput.DateTimeCells.getISOStringFromValues(
                form.watch('data.removalDate'),
                projectTimeZone
              )}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.facilityOperatorName && (
          <FormItem
            label="Operator"
            valueAdd={{
              fieldName: 'facilityOperatorName',
              ...dataSourceTracking?.facilityOperatorName
            }}
          >
            <FormInput.Text
              label="Operator"
              name="data.facilityOperatorName"
              simpleDataValidationRules={CheckWasteFacilityOperator}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.facilitySiteAddress && (
          <FormItem
            label="Site address"
            valueAdd={{
              fieldName: 'facilitySiteAddress',
              ...dataSourceTracking?.facilitySiteAddress
            }}
          >
            <FormInput.Text
              label="Site address"
              name="data.facilitySiteAddress"
              simpleDataValidationRules={CheckAddress}
            />
          </FormItem>
        )}

        <FormItem hidden={true}>
          <FormInput.TextArea
            name="data.facilitySiteAddressCoordinates"
            disabled={true}
          />
        </FormItem>

        <FormItem hidden={true}>
          <FormInput.TextArea
            name="data.facilitySiteAddressEntered"
            disabled={true}
          />
        </FormItem>

        <FormItem hidden={true}>
          <FormInput.TextArea
            name="data.facilitySiteAddressKnown"
            disabled={true}
          />
        </FormItem>

        {wasteFields && wasteFields.facilityPermitId && (
          <FormItem
            label="Permit"
            valueAdd={{
              fieldName: 'facilityPermitId',
              ...dataSourceTracking?.facilityPermitId
            }}
          >
            <FormInput.Text
              label="Permit"
              name="data.facilityPermitId"
              simpleDataValidationRules={CheckWastePermit}
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.clientReference && (
          <FormItem
            label="External reference"
            valueAdd={{
              fieldName: 'clientReference',
              ...dataSourceTracking?.clientReference
            }}
          >
            <FormInput.TextArea
              label="External reference"
              name="data.clientReference"
              simpleDataValidationRules={
                CheckReceiverOrSupplierOrSenderOrCarrierReference
              }
            />
          </FormItem>
        )}

        {wasteFields && wasteFields.notes && (
          <FormItem
            label="Notes"
            valueAdd={{
              fieldName: 'notes',
              ...dataSourceTracking?.notes
            }}
          >
            <FormInput.TextArea
              label="Notes"
              name="data.notes"
              simpleDataValidationRules={CheckNotes}
            />
          </FormItem>
        )}
      </SubSection>
    </>
  )
}

Waste.propTypes = {}

Waste.transformToFormData = (record, projectTimeZone) =>
  produce(record, draft => {
    draft.data.removalDate =
      PrimitiveInput.DateTimeCells.getDateTimeInputValuesFromISOString(
        draft.data.removalDate,
        projectTimeZone
      )
    draft.data.removalExitDate =
      PrimitiveInput.DateTimeCells.getDateTimeInputValuesFromISOString(
        draft.data.removalExitDate,
        projectTimeZone
      )
  })

Waste.transformFromFormData = (record, projectTimeZone) =>
  produce(record, draft => {
    draft.data.removalDate =
      PrimitiveInput.DateTimeCells.getISOStringFromValues(
        draft.data.removalDate,
        projectTimeZone
      )
    draft.data.removalExitDate =
      PrimitiveInput.DateTimeCells.getISOStringFromValues(
        draft.data.removalExitDate,
        projectTimeZone
      )
  })

Waste.validationSchema = yup.object({
  data: yup.object({
    removalDate: PrimitiveInput.DateTimeCells.yupValidator(
      'data.removalDate.value'
    ),
    consignmentId: yup.string().label('Consignment Number').min(2).required()
  })
})

export const cleanWasteRecordForUpdate = wasteRecord => {
  const cleanedRecord = _.cloneDeep(wasteRecord)

  // Remove readonly Waste record fields that are not needed to be sent to the API (some of these should not have been in the GET response)
  // NOTE: recordId is needed for the PUT URL but not in the body
  delete cleanedRecord.alerts
  delete cleanedRecord.assignedTo
  delete cleanedRecord.attachmentPages
  delete cleanedRecord.attachments
  delete cleanedRecord.canEdit
  delete cleanedRecord.connectionId
  delete cleanedRecord.createdAt
  delete cleanedRecord.dataSourceTracking
  delete cleanedRecord.estimatedAccuracy
  delete cleanedRecord.externalFeedId
  delete cleanedRecord.externalInstrumentId
  delete cleanedRecord.feedId
  delete cleanedRecord.instrumentId
  delete cleanedRecord.lastModified
  delete cleanedRecord.migrationSource
  delete cleanedRecord.modifiedBy
  delete cleanedRecord.modifier
  delete cleanedRecord.moduleTagDefinitionId
  delete cleanedRecord.projectId
  delete cleanedRecord.providerDefinitionId
  delete cleanedRecord.provisionId
  delete cleanedRecord.qcrAppliedToRaw
  delete cleanedRecord.templateName
  delete cleanedRecord.timestamp

  delete cleanedRecord.data.carrierLicencePresented
  delete cleanedRecord.data.classificationCodePresented
  delete cleanedRecord.data.defaultWasteCode
  delete cleanedRecord.data.facilityIsExempt
  delete cleanedRecord.data.facilityPermitPresented
  delete cleanedRecord.data.journey
  delete cleanedRecord.data.vehicleCarbonEmissionDetails

  if (cleanedRecord.data.items) {
    cleanedRecord.data.items.forEach(function (_, index) {
      this[index] = cleanWasteItemForUpdate(this[index])
    }, cleanedRecord.data.items)
  }
  return cleanedRecord
}

const cleanWasteItemForUpdate = wasteItem => {
  // Remove readonly Waste item fields that are not needed to be sent to the API
  delete wasteItem.measurementVolumeEstimated
  delete wasteItem.measurementWeightEstimated
  delete wasteItem.volumeEstimated
  delete wasteItem.weightEstimated
  delete wasteItem.carbonKgCO2e
  delete wasteItem.wasteManagementRoutes

  return wasteItem
}
